/**
 * Keyboard Shortcuts Hook
 * 
 * Provides keyboard shortcuts for common actions
 * Requirements: 13.1
 */

import { useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../auth/AuthProvider';

interface KeyboardShortcut {
  key: string;
  ctrlKey?: boolean;
  altKey?: boolean;
  shiftKey?: boolean;
  action: () => void;
  description: string;
}

export function useKeyboardShortcuts() {
  const navigate = useNavigate();
  const { isAuthenticated, isAdmin, isCoach } = useAuth();

  useEffect(() => {
    const shortcuts: KeyboardShortcut[] = [
      // Navigation shortcuts (Alt + key)
      {
        key: 'd',
        altKey: true,
        action: () => isAuthenticated && navigate('/dashboard'),
        description: 'Go to Dashboard',
      },
      {
        key: 'e',
        altKey: true,
        action: () => isAuthenticated && navigate('/events'),
        description: 'Go to Events',
      },
      {
        key: 'c',
        altKey: true,
        action: () => isAuthenticated && navigate('/calendar'),
        description: 'Go to Calendar',
      },
      {
        key: 'l',
        altKey: true,
        action: () => isAuthenticated && navigate('/leaderboard'),
        description: 'Go to Leaderboard',
      },
      {
        key: 'p',
        altKey: true,
        action: () => isAuthenticated && navigate('/profile'),
        description: 'Go to Profile',
      },
      // Coach shortcuts
      {
        key: 'r',
        altKey: true,
        action: () => (isCoach || isAdmin) && navigate('/riders'),
        description: 'Go to Riders (Coach/Admin)',
      },
      // Admin shortcuts
      {
        key: 'u',
        altKey: true,
        action: () => isAdmin && navigate('/admin/users'),
        description: 'Go to Users (Admin)',
      },
      {
        key: 's',
        altKey: true,
        action: () => isAdmin && navigate('/admin/season'),
        description: 'Go to Season Management (Admin)',
      },
      // Help shortcut
      {
        key: '?',
        shiftKey: true,
        action: () => {
          // Show keyboard shortcuts help modal
          const event = new CustomEvent('show-keyboard-shortcuts');
          window.dispatchEvent(event);
        },
        description: 'Show keyboard shortcuts',
      },
    ];

    const handleKeyDown = (event: KeyboardEvent) => {
      // Don't trigger shortcuts when typing in input fields
      const target = event.target as HTMLElement;
      if (
        target.tagName === 'INPUT' ||
        target.tagName === 'TEXTAREA' ||
        target.isContentEditable
      ) {
        return;
      }

      for (const shortcut of shortcuts) {
        const keyMatches = event.key.toLowerCase() === shortcut.key.toLowerCase();
        const ctrlMatches = shortcut.ctrlKey ? event.ctrlKey : !event.ctrlKey;
        const altMatches = shortcut.altKey ? event.altKey : !event.altKey;
        const shiftMatches = shortcut.shiftKey ? event.shiftKey : !event.shiftKey;

        if (keyMatches && ctrlMatches && altMatches && shiftMatches) {
          event.preventDefault();
          shortcut.action();
          break;
        }
      }
    };

    document.addEventListener('keydown', handleKeyDown);

    return () => {
      document.removeEventListener('keydown', handleKeyDown);
    };
  }, [navigate, isAuthenticated, isAdmin, isCoach]);

  return {
    shortcuts: [
      { key: 'Alt + D', description: 'Go to Dashboard' },
      { key: 'Alt + E', description: 'Go to Events' },
      { key: 'Alt + C', description: 'Go to Calendar' },
      { key: 'Alt + L', description: 'Go to Leaderboard' },
      { key: 'Alt + P', description: 'Go to Profile' },
      { key: 'Alt + R', description: 'Go to Riders (Coach/Admin)' },
      { key: 'Alt + U', description: 'Go to Users (Admin)' },
      { key: 'Alt + S', description: 'Go to Season Management (Admin)' },
      { key: 'Shift + ?', description: 'Show keyboard shortcuts' },
    ],
  };
}
